import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Trash2, AlertTriangle, X as XIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import LegalSection from '../components/settings/LegalSection';

/**
 * Tab "Rechtliches" — LegalSection (Impressum/Datenschutz/AVV/DSGVO export)
 * PLUS Hauptstandort-only "Account löschen" with typed confirmation.
 *
 * NOTE: LegalSection itself owns the form state for impressum/datenschutz.
 * We pass setF/form via a thin local-state shim and trigger save on demand.
 */
export default function SettingsLegalPage() {
  const { t } = useTranslation();
  const { user, practice, setPractice } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (!practice) return;
    setForm({ impressum: practice.impressum || '', datenschutz: practice.datenschutz || '' });
  }, [practice]);

  function setF(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  async function saveLegal() {
    setSaving(true);
    try {
      const r = await api.patch(`/api/practices/${practice._id}`, { impressum: form.impressum, datenschutz: form.datenschutz });
      setPractice(r.data.practice);
      toast.success(t('settings.saveSuccess'));
    } catch (_) { toast.error(t('errors.unknown')); }
    finally { setSaving(false); }
  }

  if (!practice) return null;
  const isMain = practice.is_main_location === true;

  return (
    <div className="space-y-6">
      {!isMain && (
        <div className="rounded-md border border-accent/30 bg-accent/5 p-3 text-xs text-text-secondary" data-testid="legal-branch-notice">
          {t('settings.legal.avvManagedByMain')}
        </div>
      )}

      <LegalSection form={form} setF={setF} t={t}/>

      <div className="flex justify-end">
        <button onClick={saveLegal} disabled={saving} className="so-btn so-btn-primary text-sm" data-testid="legal-save-btn">
          {saving ? '…' : t('settings.saveAll')}
        </button>
      </div>

      {isMain && user?.role === 'admin' && <AccountDeleteSection t={t}/>}
    </div>
  );
}

function AccountDeleteSection({ t }) {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <section className="so-card p-5 border-danger/30" data-testid="account-delete-section">
        <h2 className="text-sm font-semibold text-danger flex items-center gap-2 mb-2"><Trash2 size={14}/>{t('settings.accountDelete.title')}</h2>
        <p className="text-xs text-text-secondary mb-4">{t('settings.accountDelete.hint')}</p>
        <button onClick={() => setShowModal(true)} className="so-btn text-sm border border-danger text-danger hover:bg-danger/5" data-testid="account-delete-btn">
          {t('settings.accountDelete.btn')}
        </button>
      </section>
      {showModal && <DeleteModal onClose={() => setShowModal(false)} t={t}/>}
    </>
  );
}

function DeleteModal({ onClose, t }) {
  const [phrase, setPhrase] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const enabled = phrase.trim().toUpperCase() === 'LOESCHEN' || phrase.trim().toUpperCase() === 'LÖSCHEN';
  async function confirm() {
    setSubmitting(true);
    try {
      const r = await api.post('/api/stripe/account-delete', { confirm: phrase.trim() });
      toast.success(t('settings.accountDelete.success'));
      onClose();
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.message || t('errors.unknown')); }
    finally { setSubmitting(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="account-delete-modal">
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-base font-semibold text-danger flex items-center gap-2"><AlertTriangle size={18}/>{t('settings.accountDelete.modalTitle')}</h3>
          <button onClick={onClose}><XIcon size={18} className="text-text-secondary"/></button>
        </div>
        <p className="text-sm text-text">{t('settings.accountDelete.modalBody')}</p>
        <div className="mt-4">
          <label className="block text-xs font-medium text-text-secondary mb-1">{t('settings.accountDelete.modalConfirmLabel')}</label>
          <input value={phrase} onChange={(e) => setPhrase(e.target.value)} className="so-input font-mono uppercase" placeholder="LOESCHEN" data-testid="account-delete-phrase"/>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose} className="so-btn so-btn-secondary text-sm">{t('common.cancel')}</button>
          <button onClick={confirm} disabled={!enabled || submitting} className="so-btn text-sm bg-danger text-white disabled:opacity-40 hover:opacity-90" data-testid="account-delete-confirm">
            {submitting ? '…' : t('settings.accountDelete.modalConfirmBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
